
import classNames from 'classnames';

import MarkInfoIcon from '@uikit/icons/MarkInfo';

import type { RewardPool } from '@shared/api/types/rewardPools';
import { REWARD_POOL_TAG } from '@shared/api/types/rewardPools';
import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import NextLink from '@shared/common/components/NextLink';
import useRerenderAfterDate from '@shared/hooks/useRerenderAfterDate';

import { JoinRewardPoolButton } from './JoinRewardPoolButton';
import RewardPoolCardParticipationTag from './RewardPoolCardParticipationTag';
import { RewardPoolCardReward } from './RewardPoolCardReward';
import { RewardPoolCardTags } from './RewardPoolCardTags';
import { RewardPoolOptionsButton } from './RewardPoolOptionsButton/RewardPoolOptionsButton';

interface RewardPoolsCardProps {
  rewardPool: RewardPool;
  className?: string;
}

const getPoolCardClassNames = (
  rewardPool: RewardPool,
): { rootClassName: string; bodyClassName: string } => {
  const isFeatured = rewardPool.tags?.includes(REWARD_POOL_TAG.FEATURED);
  const isLastChance = rewardPool.tags?.includes(REWARD_POOL_TAG.LAST_CHANCE);
  const isHot = rewardPool.tags?.includes(REWARD_POOL_TAG.HOT);
  const isHugeReward = rewardPool.tags?.includes(REWARD_POOL_TAG.HUGE_REWARD);
  const isTrending = rewardPool.tags?.includes(REWARD_POOL_TAG.TRENDING);

  if (isFeatured) {
    return {
      rootClassName: 'border-[#C8D8F9]',
      bodyClassName: 'to-[#F6F9FE]',
    };
  } else if (isTrending) {
    return {
      rootClassName: 'border-[#ABEDB6]',
      bodyClassName: 'to-[#EEFBF0]',
    };
  } else if (isHugeReward) {
    return {
      rootClassName: 'border-[#F9E2B4]',
      bodyClassName: 'to-[#FDF8EC]',
    };
  } else if (isLastChance) {
    return {
      rootClassName: 'border-[#F0C9F7]',
      bodyClassName: 'to-[#FCF6FE]',
    };
  } else if (isHot) {
    return {
      rootClassName: 'border-[#F7CDC9]',
      bodyClassName: 'to-[#FEF7F6]',
    };
  }

  return {
    rootClassName: 'border-base-border-medium',
    bodyClassName: 'to-[#fff]',
  };
};

export const rewardPoolCardHeightClassName = 'min-h-[25rem] xl:min-h-[26.75rem]';

export const rewardPoolCardRootBaseClassName = `rounded-3xl ${rewardPoolCardHeightClassName}`;

export const RewardPoolCard = ({ rewardPool, className }: RewardPoolsCardProps) => {
  const styleClassNames = getPoolCardClassNames(rewardPool);

  // NOTE: rerender component because some parts of ui depends on those dates
  useRerenderAfterDate(rewardPool.start_date);
  useRerenderAfterDate(rewardPool.end_date);

  return (
    <>
      <div
        className={classNames(
          'flex flex-col border-2 overflow-hidden',
          rewardPoolCardRootBaseClassName,
          styleClassNames.rootClassName,
          className,
        )}
        data-qa={`pool_id-${rewardPool.id}`}
      >
        <RewardPoolCardReward rewardPool={rewardPool} />

        <div
          className={classNames(
            'bg-gradient-to-b from-[#fff] flex flex-grow flex-col gap-4 p-4 xl:p-6',
            styleClassNames.bodyClassName,
          )}
        >
          <RewardPoolCardParticipationTag rewardPool={rewardPool} />

          <div className="flex gap-2 items-baseline">
            <p className="text-sm font-semibold h-10 flex-1 xl:text-base xl:h-12 line-clamp-2 text-[#09090B]">
              {rewardPool.name}
            </p>

            {!!rewardPool.description && (
              <BaseInfoTooltip
                icon={
                  <span className="flex text-[#C2C2D6] flex-shrink-0 items-center justify-center size-5 xl:size-6">
                    <MarkInfoIcon className="icon-xs" />
                  </span>
                }
                content={rewardPool.description}
              />
            )}
          </div>

          <RewardPoolCardTags rewardPool={rewardPool} />

          <div className="flex flex-wrap gap-3 mt-auto pt-1">
            <RewardPoolOptionsButton rewardPool={rewardPool} />

            <NextLink
              href={`/reward-pools/${rewardPool.id}`}
              className="msq-btn msq-btn-md msq-btn-secondary flex-1 whitespace-nowrap"
              data-qa="details_button"
            >
              View Details
            </NextLink>

            <JoinRewardPoolButton rewardPool={rewardPool} />
          </div>
        </div>
      </div>
    </>
  );
};
